import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, type LucideIcon } from 'lucide-react';

export type TextFieldVariant = 'default' | 'otp';

const VARIANT_INPUT: Record<TextFieldVariant, string> = {
  /** Standard pill-shaped auth input — 11px radius, 14px body text. */
  default:
    'rounded-[11px] py-3 text-[14px] placeholder-slate-400 dark:placeholder-slate-500',
  /** OTP / verification code input — center-aligned, monospaced, larger. */
  otp:
    'rounded-[11px] py-3 text-center font-mono text-[18px] tracking-[0.4em] placeholder-slate-300 dark:placeholder-slate-600',
};

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /** Optional Lucide icon rendered on the left of the input. */
  leftIcon?: LucideIcon;
  /** Optional right-aligned slot — used for show/hide-password buttons, units, etc. */
  rightSlot?: ReactNode;
  /** Pick the shape: default pill or OTP center-aligned. */
  variant?: TextFieldVariant;
  /** Inline label displayed above the input (optional). */
  label?: string;
  /** Inline error message shown below the input — also flips border red. */
  error?: string;
  /** Extra classes applied to the `<input>` itself. */
  className?: string;
  /** Wrapper className (the outer `<div>`). */
  wrapperClassName?: string;
}

/**
 * Standard text input used across auth forms, profile editing, and admin
 * panels — Variant B design (slate borders, blue-600 focus ring, dark-mode
 * parity). Replaces the old `AuthInput` that lived inside `LoginPage`.
 *
 * Pass `leftIcon` for a Mail / Lock / User glyph, `rightSlot` for things
 * like a show-password toggle. Use `variant='otp'` for verification-code
 * inputs (center-aligned, monospaced).
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    leftIcon: Icon,
    rightSlot,
    variant = 'default',
    label,
    error,
    className = '',
    wrapperClassName = '',
    id,
    ...rest
  },
  ref,
) {
  const hasError = Boolean(error);

  const borderClass = hasError
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500/30'
    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-white/10';

  const paddingLeft = Icon ? 'pl-11' : 'pl-3.5';
  const paddingRight = rightSlot ? 'pr-12' : 'pr-3.5';

  return (
    <div className={wrapperClassName}>
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-[12.5px] font-semibold text-slate-700 dark:text-slate-300"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon
            size={17}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
        )}
        <input
          ref={ref}
          id={id}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError && id ? `${id}-error` : undefined}
          className={`w-full border bg-white text-slate-900 transition-colors focus:outline-none focus:ring-[3px] dark:bg-card-dark/80 dark:text-white ${paddingLeft} ${paddingRight} ${VARIANT_INPUT[variant]} ${borderClass} ${className}`}
          {...rest}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>
        )}
      </div>
      {hasError && (
        <div id={id ? `${id}-error` : undefined} className="mt-2">
          <FieldError message={error!} />
        </div>
      )}
    </div>
  );
});

interface FieldErrorProps {
  message: string;
  className?: string;
}

/**
 * Inline error banner — red pill with an alert icon, used for form validation
 * messages. Re-exported separately so callers can place it manually outside a
 * `<TextField>` (e.g. for form-level errors).
 */
export function FieldError({ message, className = '' }: FieldErrorProps) {
  return (
    <div
      role="alert"
      className={`flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400 ${className}`}
    >
      <AlertCircle size={15} aria-hidden="true" />
      {message}
    </div>
  );
}
